<template>
  <div class="py-4 container-fluid">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h5 class="mb-1">倉庫管理</h5>
        <p class="text-sm text-muted mb-0">管理門市與線上倉庫，並分配各商品的寄賣庫存</p>
      </div>
      <button class="btn btn-primary btn-sm" @click="openCreateModal">
        <i class="fas fa-plus me-1"></i> 新增倉庫
      </button>
    </div>

    <!-- Warehouse List -->
    <div class="card">
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status"></div>
        </div>

        <div v-else-if="warehouses.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-warehouse fa-3x mb-3 d-block opacity-4"></i>
          尚無倉庫，請新增第一個倉庫
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="bg-light">
              <tr>
                <th class="ps-4 text-xs text-uppercase text-muted fw-bold py-3">倉庫名稱</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3">代碼</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3">類型</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3">城市</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">剩餘在店合計</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-center">狀態</th>
                <th class="text-xs text-uppercase text-muted fw-bold py-3 text-end pe-4">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="wh in warehouses" :key="wh.id">
                <td class="ps-4">
                  <a
                    href="#"
                    class="text-dark fw-bold text-sm"
                    @click.prevent="$router.push(`/warehouses/${wh.id}`)"
                  >
                    {{ wh.name }}
                  </a>
                  <div v-if="wh.address" class="text-xs text-muted">{{ wh.address }}</div>
                </td>
                <td class="text-sm font-monospace">{{ wh.code }}</td>
                <td>
                  <span
                    class="badge"
                    :class="wh.type === 'online' ? 'bg-info' : 'bg-warning text-dark'"
                  >
                    {{ wh.type === 'online' ? '線上商城' : '實體門市' }}
                  </span>
                </td>
                <td class="text-sm">{{ wh.city || '—' }}</td>
                <td class="text-center text-sm">{{ wh.remaining_in_store_total ?? 0 }}</td>
                <td class="text-center">
                  <span class="badge" :class="wh.is_active ? 'bg-success' : 'bg-secondary'">
                    {{ wh.is_active ? '啟用' : '停用' }}
                  </span>
                </td>
                <td class="text-end pe-4">
                  <button
                    class="btn btn-sm btn-outline-secondary me-1"
                    @click="openEditModal(wh)"
                    title="編輯"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    class="btn btn-sm"
                    :class="wh.is_active ? 'btn-outline-warning' : 'btn-outline-success'"
                    @click="toggleActive(wh)"
                    :title="wh.is_active ? '停用' : '啟用'"
                  >
                    <i class="fas" :class="wh.is_active ? 'fa-pause' : 'fa-play'"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger ms-1"
                    @click="confirmDelete(wh)"
                    title="刪除"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-dialog-custom">
        <div class="card shadow-lg">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <h6 class="mb-0">{{ editTarget ? '編輯倉庫' : '新增倉庫' }}</h6>
            <button class="btn-close" @click="closeModal"></button>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label text-sm">倉庫名稱 <span class="text-danger">*</span></label>
              <input v-model="form.name" class="form-control" placeholder="例：台北門市" />
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="form-label text-sm">代碼 <span class="text-danger">*</span></label>
                <input v-model="form.code" class="form-control font-monospace" placeholder="例：TPE-001" />
              </div>
              <div class="col-6 mb-3">
                <label class="form-label text-sm">類型 <span class="text-danger">*</span></label>
                <select v-model="form.type" class="form-select">
                  <option value="physical_store">實體門市</option>
                  <option value="online">線上商城</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-4 mb-3">
                <label class="form-label text-sm">城市</label>
                <input v-model="form.city" class="form-control" placeholder="例：台北市" />
              </div>
              <div class="col-8 mb-3">
                <label class="form-label text-sm">地址</label>
                <input v-model="form.address" class="form-control" placeholder="詳細地址（選填）" />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label text-sm">備註</label>
              <textarea v-model="form.notes" class="form-control" rows="2" placeholder="選填"></textarea>
            </div>
            <div v-if="formError" class="alert alert-danger py-2 text-sm">{{ formError }}</div>
          </div>
          <div class="card-footer d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="closeModal">取消</button>
            <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveWarehouse">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ editTarget ? '儲存' : '新增' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <div v-if="deleteTarget" class="modal-backdrop-custom" @click.self="deleteTarget = null">
      <div class="modal-dialog-custom" style="max-width: 420px">
        <div class="card shadow-lg">
          <div class="card-body text-center py-4">
            <i class="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <h6>確定刪除「{{ deleteTarget.name }}」？</h6>
            <p class="text-sm text-muted">此操作將一併移除所有相關的庫存分配記錄。</p>
          </div>
          <div class="card-footer d-flex justify-content-center gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="deleteTarget = null">取消</button>
            <button class="btn btn-danger btn-sm" :disabled="deleting" @click="deleteWarehouse">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
              確認刪除
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";

export default {
  name: "Warehouses",
  data() {
    return {
      warehouses: [],
      loading: false,
      showModal: false,
      editTarget: null,
      deleteTarget: null,
      saving: false,
      deleting: false,
      formError: null,
      form: {
        name: "",
        code: "",
        type: "physical_store",
        city: "",
        address: "",
        notes: "",
      },
    };
  },
  async created() {
    await this.fetchWarehouses();
  },
  methods: {
    async fetchWarehouses() {
      this.loading = true;
      try {
        const res = await request.get("/api/admin/warehouses");
        this.warehouses = res.data.data;
      } catch (e) {
        this.$toast?.error("載入倉庫失敗");
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editTarget = null;
      this.formError = null;
      this.form = { name: "", code: "", type: "physical_store", city: "", address: "", notes: "" };
      this.showModal = true;
    },
    openEditModal(wh) {
      this.editTarget = wh;
      this.formError = null;
      this.form = {
        name: wh.name,
        code: wh.code,
        type: wh.type,
        city: wh.city || "",
        address: wh.address || "",
        notes: wh.notes || "",
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editTarget = null;
    },
    async saveWarehouse() {
      this.formError = null;
      if (!this.form.name || !this.form.code) {
        this.formError = "倉庫名稱與代碼為必填欄位";
        return;
      }
      this.saving = true;
      try {
        if (this.editTarget) {
          await request.put(`/api/admin/warehouses/${this.editTarget.id}`, this.form);
          this.$toast?.success("倉庫已更新");
        } else {
          await request.post("/api/admin/warehouses", this.form);
          this.$toast?.success("倉庫已新增");
        }
        this.closeModal();
        await this.fetchWarehouses();
      } catch (e) {
        this.formError = e.response?.data?.message || "儲存失敗，請確認代碼是否重複";
      } finally {
        this.saving = false;
      }
    },
    async toggleActive(wh) {
      try {
        await request.put(`/api/admin/warehouses/${wh.id}`, { is_active: !wh.is_active });
        this.$toast?.success(wh.is_active ? "已停用" : "已啟用");
        await this.fetchWarehouses();
      } catch (e) {
        this.$toast?.error("操作失敗");
      }
    },
    confirmDelete(wh) {
      this.deleteTarget = wh;
    },
    async deleteWarehouse() {
      this.deleting = true;
      try {
        await request.delete(`/api/admin/warehouses/${this.deleteTarget.id}`);
        this.$toast?.success("倉庫已刪除");
        this.deleteTarget = null;
        await this.fetchWarehouses();
      } catch (e) {
        this.$toast?.error("刪除失敗");
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-dialog-custom {
  width: 100%;
  max-width: 560px;
}
</style>
